import {
	CSSProperties,
	RefObject,
	useCallback,
	useLayoutEffect,
	useState
} from 'react'

export type FloatingPlacement = 'top' | 'bottom' | 'left' | 'right'
export type FloatingAlign = 'start' | 'center' | 'end'

interface UseFloatingPositionOptions {
	open: boolean
	placement?: FloatingPlacement
	align?: FloatingAlign
	offset?: number
	viewportPadding?: number
}

interface FloatingPosition {
	/** Apply to the floating element. Always `position: fixed`. */
	style: CSSProperties
	/** Placement after collision flipping — may differ from the requested one. */
	placement: FloatingPlacement
	/** Measured width of the anchor, for `matchTriggerWidth`-style sizing. */
	anchorWidth: number
	/** Vertical space left for the floating element on the resolved side. */
	availableHeight: number
}

const opposite: Record<FloatingPlacement, FloatingPlacement> = {
	top: 'bottom',
	bottom: 'top',
	left: 'right',
	right: 'left'
}

function clamp (value: number, min: number, max: number): number {
	return Math.min(Math.max(value, min), Math.max(min, max))
}

/** Cross-axis start coordinate for a floating element of `size` against an anchor span. */
function alignOnAxis (align: FloatingAlign, start: number, length: number, size: number): number {
	if (align === 'start') return start
	if (align === 'end') return start + length - size
	return start + length / 2 - size / 2
}

/**
 * Positions a floating element (tooltip, popover) against an anchor using
 * viewport-fixed coordinates, flipping to the opposite side and shifting along
 * the cross axis when the preferred placement would overflow the viewport.
 *
 * Shifting is deliberately cross-axis only, so the element stays welded to its
 * anchor and scrolls away with it rather than sticking to the viewport edge.
 * Main-axis fit is handled by flipping plus the returned `availableHeight`.
 *
 * `position: fixed` is what makes the element immune to ancestor `overflow`
 * clipping — the floating node must also be rendered through `FloatingPortal`
 * so it escapes any ancestor that establishes a containing block.
 */
export function useFloatingPosition (
	anchorRef: RefObject<HTMLElement | null>,
	floatingRef: RefObject<HTMLElement | null>,
	{
		open,
		placement = 'top',
		align = 'center',
		offset = 8,
		viewportPadding = 8
	}: UseFloatingPositionOptions
): FloatingPosition {
	const [position, setPosition] = useState<FloatingPosition>({
		style: { position: 'fixed', top: 0, left: 0, visibility: 'hidden' },
		placement,
		anchorWidth: 0,
		availableHeight: 0
	})

	const update = useCallback(() => {
		const anchor = anchorRef.current
		const floating = floatingRef.current
		if (!anchor || !floating) return

		const a = anchor.getBoundingClientRect()
		const f = floating.getBoundingClientRect()
		const vw = document.documentElement.clientWidth
		const vh = document.documentElement.clientHeight

		const space: Record<FloatingPlacement, number> = {
			top: a.top - viewportPadding,
			bottom: vh - a.bottom - viewportPadding,
			left: a.left - viewportPadding,
			right: vw - a.right - viewportPadding
		}

		const vertical = placement === 'top' || placement === 'bottom'
		const needed = (vertical ? f.height : f.width) + offset

		let resolved = placement
		if (
			space[placement] < needed &&
			space[opposite[placement]] > space[placement]
		) {
			resolved = opposite[placement]
		}

		let top = 0
		let left = 0

		if (resolved === 'top' || resolved === 'bottom') {
			top = resolved === 'top' ? a.top - f.height - offset : a.bottom + offset
			left = clamp(alignOnAxis(align, a.left, a.width, f.width), viewportPadding, vw - f.width - viewportPadding)
		} else {
			left = resolved === 'left' ? a.left - f.width - offset : a.right + offset
			top = clamp(alignOnAxis(align, a.top, a.height, f.height), viewportPadding, vh - f.height - viewportPadding)
		}

		const anchorWidth = Math.round(a.width)
		const availableHeight = Math.round(
			resolved === 'top' || resolved === 'bottom'
				? space[resolved] - offset
				: vh - viewportPadding * 2
		)

		top = Math.round(top)
		left = Math.round(left)

		setPosition(prev => {
			if (
				prev.placement === resolved &&
				prev.anchorWidth === anchorWidth &&
				prev.availableHeight === availableHeight &&
				prev.style.top === top &&
				prev.style.left === left &&
				prev.style.visibility === 'visible'
			) {
				return prev
			}
			return {
				style: { position: 'fixed', top, left, visibility: 'visible' },
				placement: resolved,
				anchorWidth,
				availableHeight
			}
		})
	}, [anchorRef, floatingRef, placement, align, offset, viewportPadding])

	useLayoutEffect(() => {
		if (!open) {
			setPosition(prev =>
				prev.style.visibility === 'hidden'
					? prev
					: { ...prev, style: { ...prev.style, visibility: 'hidden' } }
			)
			return
		}

		// Position before the first paint, then re-measure every frame rather than
		// listening for scroll/resize. The anchor also moves on layout shifts that
		// fire no event at all — a lazy image or the SightMap iframe settling above
		// it, for example — which would otherwise strand the floating element. The
		// cost is two getBoundingClientRect calls per frame, only while something
		// is open.
		update()

		let frame = 0
		const loop = () => {
			update()
			frame = requestAnimationFrame(loop)
		}
		frame = requestAnimationFrame(loop)

		return () => { cancelAnimationFrame(frame) }
	}, [open, update])

	return position
}
